/**
 * Действия со строкой договора (право `contract_edit`, **пишет в боевую CRM**): «изменить» — инлайн-форма
 * цены, дней проката, продажи, комментария и подшива (уходят только изменённые поля), «удалить» —
 * с подтверждением и обязательной причиной; у удалённой строки — «восстановить». Версия — из `GET /api/contracts/{id}`.
 */
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { numCls } from "@/components/contract/ui";
import {
  type ContractWriteResult,
  type RawContractRow,
  DAYS_MAX,
  DELETE_REASON_MAX,
  DELETE_REASON_MIN,
  PRICE_MAX,
  deleteContractRow,
  patchContractRow,
  restoreContractRow,
  useContractRefresh,
  writeError,
} from "@/lib/contract";
import { btn } from "@/lib/panelBtn";

/** Строка договора для правки: сырая строка CRM, версия договора, сделка и чат. */
export type RowEditCtx = {
  row: RawContractRow;
  dogovorId: number;
  version: string;
  dealId: number;
  chatId: number | null;
  /** Строка-услуга по `deal-contract` (`ContractProduct.service`); итог — `isService`. */
  service: boolean;
  /** Итог удачной записи — показывает карточка договора (строка после удаления уходит из списка). */
  onDone: (ok: string, r: ContractWriteResult) => void;
};

/** Артикул строк-услуг CRM — тем же правилом бэкенд решает «услуга» (`write._услуга`). На экран не выводим. */
const SERVICE_ARTICLE = 700700;
/** Услуга: у неё нет подшива, дней проката и продажи. */
const isService = (ctx: RowEditCtx) => ctx.service || ctx.row.article === SERVICE_ARTICLE;

const COMMENT_MAX = 512;
const link = "text-[12px] text-accent-text hover:underline disabled:opacity-50";

/** Целое из поля ввода: пусто — `null`, не целое или вне 0..max — `undefined` (ошибка). */
function whole(s: string, max: number): number | null | undefined {
  const t = s.trim();
  if (!t) return null;
  if (!/^\d+$/.test(t)) return undefined;
  const n = Number(t);
  return n <= max ? n : undefined;
}

function EditForm({ ctx, onClose }: { ctx: RowEditCtx; onClose: () => void }) {
  // Снимок строки на открытии формы: после 409 договор перечитан, но «изменено» — только то, что
  // правил сотрудник, иначе нетронутые поля со старыми значениями откатили бы правку коллеги.
  const [row] = useState(() => ctx.row);
  const service = isService(ctx);
  const podzakaz = Boolean(row.podzakazID);
  const refresh = useContractRefresh(ctx.dealId, ctx.dogovorId, ctx.chatId);
  const [price, setPrice] = useState(row.priceAccount == null ? "" : String(row.priceAccount));
  const [days, setDays] = useState(row.priceDay == null ? "" : String(row.priceDay));
  const [sale, setSale] = useState(Boolean(row.isSale));
  const [comment, setComment] = useState(row.productComment ?? "");
  const [podwiv, setPodwiv] = useState(Boolean(row.podwiv));

  const p = whole(price, PRICE_MAX);
  const d = whole(days, DAYS_MAX);
  const problems = [
    p === undefined && "цена — целое число рублей",
    d === undefined && `дней проката — целое от 0 до ${DAYS_MAX}`,
  ].filter(Boolean) as string[];
  // Только изменённое: пустой комментарий и `null` в CRM — одно и то же.
  const changed: Record<string, unknown> = {};
  if (p !== undefined && p !== row.priceAccount) changed.priceAccount = p;
  if (!service) {
    if (d !== undefined && d !== row.priceDay) changed.priceDay = d;
    if (!podzakaz && Number(sale) !== row.isSale) changed.isSale = Number(sale);
    if (!podzakaz && Number(podwiv) !== row.podwiv) changed.podwiv = Number(podwiv);
  }
  const text = comment.trim() || null;
  if (text !== (row.productComment?.trim() || null)) changed.productComment = text;
  const empty = Object.keys(changed).length === 0;

  const save = useMutation({
    mutationFn: () => patchContractRow(ctx.dogovorId, row.id, changed, ctx.version, ctx.chatId),
    onSuccess: (r) => (ctx.onDone("Строка записана в CRM", r), onClose()),
    onSettled: refresh,
  });
  const ready = !empty && problems.length === 0 && !save.isPending;
  return (
    <form
      className="flex w-full flex-col gap-2 rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-2 text-[12.5px]"
      onSubmit={(e) => (e.preventDefault(), ready && save.mutate())}
      onKeyDown={(e) => e.key === "Escape" && !save.isPending && onClose()}
      data-annot={`правка строки договора ${row.id}`}
    >
      <fieldset disabled={save.isPending} className="flex flex-col gap-2">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-0.5">
            <span className="text-fg-muted">Цена, ₽</span>
            <input className={`${numCls} w-28`} inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} autoFocus />
          </label>
          {!service && (
            <label className="flex flex-col gap-0.5">
              <span className="text-fg-muted">Дней проката</span>
              <input className={`${numCls} w-20`} inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)} placeholder="—" />
            </label>
          )}
          {!service && (
            <div className="flex flex-col gap-0.5">
              <span className="text-fg-muted">Вид</span>
              <div className="flex" role="radiogroup" aria-label="Прокат или продажа" title={podzakaz ? "У подзаказа продажу не меняют" : undefined}>
                {[false, true].map((v) => (
                  <button
                    key={String(v)}
                    type="button"
                    role="radio"
                    aria-checked={sale === v}
                    disabled={podzakaz}
                    onClick={() => setSale(v)}
                    className={`h-8 border px-2.5 first:rounded-l-[var(--radius-ui)] last:rounded-r-[var(--radius-ui)] disabled:cursor-not-allowed disabled:opacity-50 ${
                      sale === v ? "border-accent bg-accent text-white" : "border-border hover:bg-bg-hover"
                    }`}
                  >
                    {v ? "Продажа" : "Прокат"}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!service && !podzakaz && (
            <label className="flex h-8 items-center gap-1.5">
              <input type="checkbox" checked={podwiv} onChange={(e) => setPodwiv(e.target.checked)} />
              Подшив
            </label>
          )}
        </div>
        <label className="flex flex-col gap-0.5">
          <span className="text-fg-muted">
            Комментарий <span className="text-fg-faint">({comment.length}/{COMMENT_MAX})</span>
          </span>
          <textarea
            rows={2}
            maxLength={COMMENT_MAX}
            className="w-full resize-y rounded-[var(--radius-ui)] border border-border bg-transparent px-2 py-1 leading-snug hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </label>
      </fieldset>
      {problems.length > 0 && <div className="text-err">{problems.join(" · ")}</div>}
      {save.isError && <div className="whitespace-pre-wrap text-err">{writeError(save.error)}</div>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" className={btn.primary} disabled={!ready}>
          {save.isPending ? "Записываю в CRM…" : "Сохранить"}
        </button>
        <button type="button" className={btn.quiet} onClick={onClose} disabled={save.isPending}>
          Отмена
        </button>
        {empty && problems.length === 0 && <span className="text-[11.5px] text-fg-muted">Изменений нет</span>}
      </div>
    </form>
  );
}

/**
 * Удалить — с подтверждением и обязательной причиной (владелец 23.10.26): причина уходит в журнал prokat
 * и в историю CRM; запись идёт в боевую CRM, связанная подшивка удаляется вместе со строкой, затем
 * синхронизация с Bitrix и сверка сделки — итог показывает карточка договора.
 */
function DeleteAsk({ ctx, onClose }: { ctx: RowEditCtx; onClose: () => void }) {
  const refresh = useContractRefresh(ctx.dealId, ctx.dogovorId, ctx.chatId);
  const [reason, setReason] = useState("");
  const text = reason.trim().replace(/\s+/g, " ");
  const ready = text.length >= DELETE_REASON_MIN && text.length <= DELETE_REASON_MAX;
  const m = useMutation({
    mutationFn: () => deleteContractRow(ctx.dogovorId, ctx.row.id, ctx.version, text, ctx.chatId),
    onSuccess: (r) => (ctx.onDone("Строка удалена из договора", r), onClose()),
    onSettled: refresh,
  });
  return (
    <form
      className="flex w-full flex-col gap-1.5 rounded-[var(--radius-ui)] border border-err/40 bg-err/10 p-2 text-[12.5px]"
      onSubmit={(e) => (e.preventDefault(), ready && !m.isPending && m.mutate())}
      onKeyDown={(e) => e.key === "Escape" && !m.isPending && onClose()}
      data-annot={`удаление строки договора ${ctx.row.id}`}
    >
      <span className="font-medium">Удалить строку из договора? Запишется в боевую CRM и в сделку Bitrix.</span>
      <label className="flex flex-col gap-0.5">
        <span className="text-fg-muted">
          Почему удаляете — обязательно <span className="text-fg-faint">({text.length}/{DELETE_REASON_MAX})</span>
        </span>
        <textarea
          rows={2}
          maxLength={DELETE_REASON_MAX + 50}
          autoFocus
          disabled={m.isPending}
          placeholder="Например: клиент выбрал другое платье"
          className="w-full resize-y rounded-[var(--radius-ui)] border border-border bg-bg px-2 py-1 leading-snug hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      {text.length > DELETE_REASON_MAX && <div className="text-err">Причина длиннее {DELETE_REASON_MAX} знаков</div>}
      {m.isError && <div className="whitespace-pre-wrap text-err">{writeError(m.error)}</div>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" disabled={!ready || m.isPending} className={btn.primary}>
          {m.isPending ? "Удаляю и сверяю с Bitrix…" : "Да, удалить"}
        </button>
        <button type="button" disabled={m.isPending} onClick={onClose} className={btn.quiet}>
          Нет
        </button>
        {!ready && !m.isPending && text.length < DELETE_REASON_MIN && <span className="text-[11.5px] text-fg-muted">Сначала напишите причину</span>}
      </div>
    </form>
  );
}

/**
 * Удалённая строка: «восстановить» — новой строкой-копией, как CRM. После удачи ссылки нет, пока
 * перечитывание не обновит список: второй клик завёл бы вторую копию (услугам дубли разрешены).
 */
function Restore({ ctx }: { ctx: RowEditCtx }) {
  const refresh = useContractRefresh(ctx.dealId, ctx.dogovorId, ctx.chatId);
  const m = useMutation({
    mutationFn: () => restoreContractRow(ctx.dogovorId, ctx.row.id, ctx.version, ctx.chatId),
    onSuccess: (r) => ctx.onDone("Строка восстановлена", r),
    onSettled: refresh,
  });
  if (m.isSuccess) return <span className="text-[12px] text-ok">Восстановлено</span>;
  return (
    <div className="flex flex-col gap-0.5">
      <button type="button" disabled={m.isPending} onClick={() => m.mutate()} className={`${link} self-start`}>
        {m.isPending ? "Восстанавливаю…" : "восстановить"}
      </button>
      {m.isError && <div className="whitespace-pre-wrap text-[12px] text-err">{writeError(m.error)}</div>}
    </div>
  );
}

/** Кнопки строки договора; права проверяет вызывающий. */
export function RowActions({ ctx }: { ctx: RowEditCtx }) {
  const [mode, setMode] = useState<"edit" | "delete" | null>(null);
  if (ctx.row.isDeleted) return <Restore ctx={ctx} />;
  if (mode === "edit") return <EditForm ctx={ctx} onClose={() => setMode(null)} />;
  if (mode === "delete") return <DeleteAsk ctx={ctx} onClose={() => setMode(null)} />;
  return (
    <div className="flex flex-wrap items-center gap-x-3">
      <button type="button" onClick={() => setMode("edit")} className={link}>
        изменить
      </button>
      <button type="button" onClick={() => setMode("delete")} className="text-[12px] text-fg-muted hover:text-err hover:underline">
        удалить
      </button>
    </div>
  );
}
